import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { Tenant, TenantType } from '@/types'
import { 
  TenantInfo, 
  resolveTenantFromUrl,
  validateTenantAccess,
  clearTenantCache,
  isTenantFeatureEnabled,
  getTenantAwareApiBaseUrl,
  getTenantUrl as buildTenantUrl
} from '@/lib/tenant-detection'
import { applyTenantBranding } from '@/lib/tenant-branding'

interface TenantContextType {
  currentTenant: Tenant | null
  tenantInfo: TenantInfo | null // New provisioning-aware tenant info
  tenantType: TenantType | null
  isClient: boolean
  isCreator: boolean
  isInternal: boolean
  isLoading: boolean
  error: string | null
  switchTenant: (tenantId: string) => Promise<void>
  getTenantConfig: () => any
  refreshTenant: () => Promise<void>
  // New provisioning-aware methods
  isFeatureEnabled: (feature: string) => boolean
  getTenantUrl: (path?: string) => string | null
  getApiBaseUrl: () => string
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

export const useTenant = () => {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}

interface TenantProviderProps {
  children: React.ReactNode
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { user, firebaseUser } = useAuth()
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  // Query for new provisioning-aware tenant information
  const {
    data: tenantInfo,
    isLoading: isTenantInfoLoading,
    refetch: refetchTenantInfo,
    isError: isTenantInfoError,
    error: tenantInfoError
  } = useQuery<TenantInfo | null>({
    queryKey: ['tenant-info', user?.tenantId],
    queryFn: async () => {
      try {
        setError(null)
        
        // Skip tenant detection completely in development to prevent redirect loops
        if (import.meta.env.DEV) {
          return null
        }
        
        // Skip URL-based tenant detection in development or localhost
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' || 
                           window.location.hostname.includes('localhost');
        
        if (!isLocalhost) {
          // Try to resolve tenant from URL first (for multi-tenant domains)
          const urlTenant = await resolveTenantFromUrl()
          if (urlTenant) {
            return urlTenant
          }
        }

        // Use user's tenant ID for development or as fallback
        if (user?.tenantId) {
          const { getTenantById } = await import('@/lib/tenant-detection')
          return await getTenantById(user.tenantId)
        }

        return null
      } catch (err) {
        console.error('Error resolving tenant info:', err)
        // Don't set error in development to avoid disrupting the flow
        if (!window.location.hostname.includes('localhost')) {
          setError('Failed to load tenant information')
        }
        return null // Return null instead of throwing to prevent redirect loops
      }
    },
    retry: false, // Disable retry in development
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
    refetchOnWindowFocus: false,
    enabled: !import.meta.env.DEV // Completely disable the query in development
  })

  // Legacy tenant data fetch (for backward compatibility)
  const fetchTenantData = async (tenantId: string) => {
    try {
      // For development, create mock tenant data based on tenantId
      if (import.meta.env.DEV) {
        const mockTenant: Tenant = {
          id: tenantId,
          name: `${tenantId.charAt(0).toUpperCase() + tenantId.slice(1)} Organization`,
          type: tenantId as any,
          config: {
            features: ['content', 'analytics', 'blockchain'],
            limits: {
              storage: 100 * 1024 * 1024 * 1024, // 100GB
              uploads: 1000,
              users: 50
            }
          },
          createdAt: new Date(),
          updatedAt: new Date()
        }
        setCurrentTenant(mockTenant)
        return
      }

      // Real API call for production
      if (!firebaseUser) return

      const token = await firebaseUser.getIdToken()
      const response = await fetch(`/api/tenants/${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const tenantData = await response.json()
        setCurrentTenant(tenantData)
      } else {
        console.error('Failed to fetch tenant data')
        setCurrentTenant(null)
      }
    } catch (error) {
      console.error('Error fetching tenant data:', error)
      setCurrentTenant(null)
    }
  }

  // Switch tenant
  const switchTenant = async (tenantId: string) => {
    try {
      // For development, just fetch the tenant data
      if (import.meta.env.DEV) {
        await fetchTenantData(tenantId)
        return
      }

      // Real API call for production
      if (!firebaseUser) return

      const token = await firebaseUser.getIdToken()
      const response = await fetch('/api/auth/switch-tenant', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId })
      })

      if (response.ok) {
        await fetchTenantData(tenantId)
      } else {
        console.error('Failed to switch tenant')
      }
    } catch (error) {
      console.error('Error switching tenant:', error)
    }
  }

  // Validate tenant access on tenant info change (only in production)
  useEffect(() => {
    // Apply tenant branding whenever tenant info changes
    if (tenantInfo !== undefined) {
      applyTenantBranding(tenantInfo)
    }
    
    // Skip tenant validation completely in development
    if (import.meta.env.DEV) {
      return
    }
    
    const isLocalhost = window.location.hostname.includes('localhost');
    
    if (tenantInfo && !isLocalhost) {
      const validation = validateTenantAccess(tenantInfo)
      if (!validation.isValid) {
        setError(validation.reason || 'Access denied')
        toast.error(validation.reason || 'This tenant is not accessible')
      }
    }
  }, [tenantInfo])

  // Get tenant configuration (enhanced with provisioning info)
  const getTenantConfig = useCallback(() => {
    if (tenantInfo) {
      return {
        ...tenantInfo.feature_flags,
        limits: tenantInfo.resource_limits,
        branding: tenantInfo.branding_config,
        legacy: currentTenant?.config || {}
      }
    }
    return currentTenant?.config || {}
  }, [tenantInfo, currentTenant])

  // Refresh tenant information
  const refreshTenant = useCallback(async () => {
    clearTenantCache()
    await refetchTenantInfo()
    if (user?.tenantId) {
      await fetchTenantData(user.tenantId)
    }
  }, [refetchTenantInfo, user?.tenantId])

  // Check if feature is enabled
  const isFeatureEnabled = useCallback((feature: string): boolean => {
    return isTenantFeatureEnabled(tenantInfo, feature)
  }, [tenantInfo])

  // Get tenant URL
  const getTenantUrl = useCallback((path: string = ''): string | null => {
    if (!tenantInfo) return null
    return buildTenantUrl(tenantInfo, path)
  }, [tenantInfo])

  // Get API base URL
  const getApiBaseUrl = useCallback((): string => {
    return getTenantAwareApiBaseUrl(tenantInfo)
  }, [tenantInfo])

  // Computed properties (maintain backward compatibility)
  const tenantType = currentTenant?.type || (tenantInfo ? 'client' : null)
  const isClient = tenantType === 'client'
  const isCreator = tenantType === 'creator'  
  const isInternal = tenantType === 'internal'

  // Fetch legacy tenant data when user changes (backward compatibility)
  useEffect(() => {
    if (user?.tenantId) {
      fetchTenantData(user.tenantId)
    } else {
      setCurrentTenant(null)
    }
  }, [user?.tenantId])

  // Handle query errors
  useEffect(() => {
    if (isTenantInfoError && tenantInfoError) {
      const errorMessage = tenantInfoError instanceof Error 
        ? tenantInfoError.message 
        : 'Failed to load tenant information'
      setError(errorMessage)
    }
  }, [isTenantInfoError, tenantInfoError])

  const value: TenantContextType = {
    currentTenant,
    tenantInfo,
    tenantType,
    isClient,
    isCreator,
    isInternal,
    isLoading: isTenantInfoLoading,
    error: error || (isTenantInfoError ? 'Failed to load tenant' : null),
    switchTenant,
    getTenantConfig,
    refreshTenant,
    isFeatureEnabled,
    getTenantUrl,
    getApiBaseUrl
  }

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  )
} 